import type { BrowserOptions } from "@sentry/browser";

import { failedMutationName } from "./mutation-errors";
import { scrubBreadcrumb, scrubEvent, scrubText } from "./sentry-privacy";

// An id or an address in a message shouldn't make each failure its own issue.
const groupingText = (message: string) =>
  (scrubText(message).split("\n", 1)[0] ?? "")
    .replace(/\S+@\S+/g, "<email>")
    .replace(
      /\b[0-9a-z]{20,}\b|\b[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\b/gi,
      "<id>",
    )
    .slice(0, 200);

export const sentryOptions = (release: string): BrowserOptions => ({
  dsn: "https://5617b598c4ba48eaaa4339b11bed0137@o304107.ingest.sentry.io/1757675",
  release,
  ignoreErrors: ["ResizeObserver loop limit exceeded"],
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSend(event, hint) {
    const originalExceptionMsg =
      typeof hint.originalException === "string"
        ? hint.originalException
        : (hint.originalException as { message?: string } | undefined)?.message;

    const isNonErrorException =
      event.exception?.values?.[0]?.value?.startsWith(
        "Non-Error exception captured",
      ) || originalExceptionMsg?.startsWith("Non-Error exception captured");

    if (isNonErrorException) {
      return null;
    }

    // Every mutation's error has the same stack, through Apollo's own code, so
    // Sentry's default put them all in one issue.
    const mutation = failedMutationName(hint.originalException);
    if (mutation) {
      event.fingerprint = [
        "mutation",
        mutation,
        groupingText(originalExceptionMsg ?? ""),
      ];
      event.tags = { ...event.tags, mutation };
    }

    return scrubEvent(event);
  },
});
